import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { MapPin, Mail, Phone, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    institution: '',
    phone: '',
    email: '',
    services_required: [],
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const serviceOptions = [
    'Robotics & IoT Lab',
    'Drone Lab Setup',
    'Drone Manufacturing',
    'DGCA RPTO',
    'Drone Soccer Arena',
    'AR/VR/AI Lab',
    '3D Printing Lab',
    'Turnkey Lab Development',
    'Training & Academic Integration',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (service) => {
    setFormData((prev) => ({
      ...prev,
      services_required: prev.services_required.includes(service)
        ? prev.services_required.filter((s) => s !== service)
        : [...prev.services_required, service],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/contact`, formData);
      toast.success('Message sent successfully! We will contact you soon.');
      setFormData({
        name: '',
        institution: '',
        phone: '',
        email: '',
        services_required: [],
        message: '',
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Error submitting contact form:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      `Hi! I'm interested in UAV Geo Solutions services.\n\nName: ${formData.name || '[Your Name]'}\nInstitution: ${formData.institution || '[Your Institution]'}\nServices: ${formData.services_required.join(', ') || '[Services Needed]'}\n\nMessage: ${formData.message || '[Your Message]'}`
    );
    window.open(`https://wa.me/919693809074?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-32 pb-24 relative">
      <SEO 
        title="Contact Us - UAV Geo Solutions | Get in Touch"
        description="Contact UAV Geo Solutions for Drone Labs, Robotics Labs, DGCA RPTO Training, and more. Located in Greater Noida. Call +91 9693809074 or email Uavgeosolutions@gmail.com"
        keywords="contact UAV Geo Solutions, drone lab inquiry, DGCA RPTO contact, Greater Noida technology labs"
        url="/contact"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-7xl font-heading font-bold uppercase tracking-tighter mb-6 text-glow">
            Contact Us
          </h1>
          <p className="text-base md:text-lg text-gray-400 font-light leading-relaxed max-w-3xl mx-auto">
            Let's discuss how we can transform your institution
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="gradient-border rounded-3xl p-8 space-y-6 bg-gradient-to-br from-[#0a0e27]/80 to-[#1a1f3a]/80 backdrop-blur-xl relative z-20">
              <h2 className="text-2xl font-heading font-semibold mb-6">Get In Touch</h2>
              
              <div className="flex items-start space-x-4">
                <MapPin size={24} className="text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-2">Office Address</h3>
                  <p className="text-gray-300">02B, Third Floor, Sector P3</p>
                  <p className="text-gray-300">Greater Noida – 201315</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Mail size={24} className="text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-2">Email</h3>
                  <a href="mailto:Uavgeosolutions@gmail.com" className="text-gray-300 hover:text-primary transition-colors relative z-30">
                    Uavgeosolutions@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <Phone size={24} className="text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-2">Phone</h3>
                  <a href="tel:+919693809074" className="text-gray-300 hover:text-primary transition-colors block relative z-30">
                    +91 9693809074
                  </a>
                  <a href="tel:+919958831758" className="text-gray-300 hover:text-primary transition-colors block relative z-30">
                    +91 9958831758
                  </a>
                </div>
              </div>

              <div className="pt-6 border-t border-white/10">
                <p className="text-xs text-gray-500 mb-1">CIN: U95120BR2024OPC066916</p>
                <p className="text-xs text-gray-500">GST: 10AADCU4826A1Z0</p>
              </div>

              {/* WhatsApp Quick Contact */}
              <div className="pt-4 relative z-40">
                <Button
                  onClick={handleWhatsApp}
                  type="button"
                  className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-bold py-6 text-base group cursor-pointer"
                  data-testid="whatsapp-contact-button"
                >
                  <MessageCircle size={22} className="mr-2 group-hover:scale-110 transition-transform" />
                  Contact via WhatsApp
                </Button>
              </div>
            </div>

            {/* Google Maps */}
            <div className="gradient-border rounded-3xl overflow-hidden h-64 relative z-10">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3507.2!2d77.5!3d28.5!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDMwJzAwLjAiTiA3N8KwMzAnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="UAV Geo Solutions Location"
              />
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <form onSubmit={handleSubmit} className="gradient-border rounded-3xl p-8 space-y-6 bg-gradient-to-br from-[#0a0e27]/80 to-[#1a1f3a]/80 backdrop-blur-xl relative z-20" data-testid="contact-form">
              <h2 className="text-2xl font-heading font-semibold mb-6">Send Us a Message</h2>

              <div className="relative z-30">
                <label className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-2 block">
                  Your Name *
                </label>
                <Input
                  name="name"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-name-input"
                  className="bg-white/10 border-2 border-white/20 focus:border-primary focus:bg-white/15 transition-all text-white placeholder:text-gray-500 rounded-lg px-4 py-3 text-base"
                />
              </div>

              <div className="relative z-30">
                <label className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-2 block">
                  Institution Name *
                </label>
                <Input
                  name="institution"
                  placeholder="Enter institution name"
                  value={formData.institution}
                  onChange={handleChange}
                  required
                  data-testid="contact-institution-input"
                  className="bg-white/10 border-2 border-white/20 focus:border-primary focus:bg-white/15 transition-all text-white placeholder:text-gray-500 rounded-lg px-4 py-3 text-base"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-30">
                <div>
                  <label className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-2 block">
                    Phone *
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    placeholder="Your phone number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="contact-phone-input"
                    className="bg-white/10 border-2 border-white/20 focus:border-primary focus:bg-white/15 transition-all text-white placeholder:text-gray-500 rounded-lg px-4 py-3 text-base"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-2 block">
                    Email *
                  </label>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Your email address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email-input"
                    className="bg-white/10 border-2 border-white/20 focus:border-primary focus:bg-white/15 transition-all text-white placeholder:text-gray-500 rounded-lg px-4 py-3 text-base"
                  />
                </div>
              </div>

              <div className="relative z-30">
                <label className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4 block">
                  Services Required
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {serviceOptions.map((service) => (
                    <button
                      key={service}
                      type="button"
                      onClick={() => handleServiceToggle(service)}
                      data-testid={`service-option-${service.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${
                        formData.services_required.includes(service)
                          ? 'bg-primary/30 border-primary text-white shadow-lg shadow-primary/30'
                          : 'bg-white/5 border-white/20 text-gray-400 hover:border-primary/50 hover:bg-white/10'
                      }`}
                    >
                      {service}
                    </button>
                  ))}
                </div>
              </div>

              <div className="relative z-30">
                <label className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-2 block">
                  Your Message *
                </label>
                <Textarea
                  name="message"
                  placeholder="Tell us about your requirements..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  data-testid="contact-message-input"
                  className="bg-white/10 border-2 border-white/20 focus:border-primary focus:bg-white/15 transition-all text-white placeholder:text-gray-500 rounded-lg resize-none px-4 py-3 text-base"
                />
              </div>

              <div className="relative z-40">
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="w-full bg-gradient-to-r from-primary to-indigo-500 text-black hover:from-primary-hover hover:to-indigo-600 font-bold py-6 text-base uppercase tracking-wider shadow-xl shadow-primary/30 group cursor-pointer"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
